import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Box, Card, Title, Text, Group, Button, Stack } from '@mantine/core';
import { StepperNavigation } from './NavigationButtons';
import NarrativePanel from './NarrativePanel';
import StepContent from './StepContent';
import AIGeneratingIcon from './AIGeneratingIcon';
import {
  setContextData,
  generateUnifiedNarrative,
  fetchContextData,
} from '../redux/actions/contextActions';

const ContextAssessmentStepper = ({ modelId, assessmentUuid }) => {
  const dispatch = useDispatch();
  
  // Redux state
  const config = useSelector((s) => s.config.configs['contextForm']);
  const configLoading = useSelector((s) => s.config.loading);
  const configError = useSelector((s) => s.config.error);
  const contextData = useSelector((s) => s.context.data[modelId] || {});
  const isLoading = useSelector((s) => s.context.loading);
  const modelMeta = useSelector((s) => s.risks.models[modelId] || {});

  // Stepper state
  const [currentStep, setCurrentStep] = useState(0);
  const [showNarrative, setShowNarrative] = useState(false);

  // Configuration
  const sections = useMemo(() => (config ? Object.keys(config) : []), [config]);
  const stepLabels = sections.map(sec => {
    const titles = {
      useCase: 'Use Case Context',
      modelContext: 'Model Context', 
      businessContext: 'Business Context'
    };
    return titles[sec] || sec;
  });

  // Check step completion
  const getStepCompletion = (stepIndex) => {
    const section = sections[stepIndex];
    if (!section || !contextData[section]) return false;
    
    // Check if section has sufficient data
    const sectionData = contextData[section];
    const requiredFields = config?.[section]?.filter(field => field.required) || [];
    
    if (requiredFields.length === 0) {
      // If no required fields defined, check if any data exists
      return Object.keys(sectionData).length > 0 && 
             Object.values(sectionData).some(value => value && value.toString().trim().length > 0);
    }
    
    // Check required fields
    return requiredFields.every(field => {
      const value = sectionData[field.name];
      return value && value.toString().trim().length > 0;
    });
  };

  const currentStepComplete = getStepCompletion(currentStep);
  const allStepsComplete = sections.every((_, idx) => getStepCompletion(idx));

  // Check if unified narrative exists
  const hasUnifiedNarrative = useMemo(() => {
    return contextData.unifiedNarrative && 
           contextData.unifiedNarrative.narrative && 
           contextData.unifiedNarrative.narrative.trim().length > 0;
  }, [contextData]);

  // Auto-show narrative panel when narrative exists
  useEffect(() => {
    if (hasUnifiedNarrative && !showNarrative) {
      setShowNarrative(true);
    }
  }, [hasUnifiedNarrative, showNarrative]);

  // Initialize context data with model metadata
  useEffect(() => {
    if (config && modelId && sections.length > 0) {
      const initial = sections.reduce((acc, sec) => {
        acc[sec] = config[sec].reduce((fAcc, f) => {
          fAcc[f.name] = f.type === 'multiselect' ? [] : f.defaultValue || '';
          return fAcc;
        }, {});
        return acc;
      }, {});
      
      // Only initialize if no context data exists yet
      if (!contextData || Object.keys(contextData).length === 0) {
        if (initial.modelContext) {
          initial.modelContext['Model Type'] = modelMeta?.modelType || '';
          initial.modelContext['Model Stage'] = modelMeta?.modelStage || '';
          if (initial.useCase) {
            initial.useCase['Use Case Description'] = modelMeta?.useCaseShortDescription || '';
          }
        }
        dispatch(setContextData(modelId, initial));
      }
    }
  }, [config, dispatch, modelId, sections, contextData, modelMeta?.modelType, modelMeta?.modelStage, modelMeta?.useCaseShortDescription]);

  // Fetch context data on mount
  useEffect(() => {
    if (modelId && assessmentUuid) {
      dispatch(fetchContextData(modelId, assessmentUuid));
    }
  }, [dispatch, modelId, assessmentUuid]);

  // Navigation handlers
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < sections.length - 1 && currentStepComplete) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Narrative handlers
  const handleGenerateNarrative = () => {
    setShowNarrative(true);
    dispatch(generateUnifiedNarrative(modelId, assessmentUuid, contextData));
  };

  const handleEditNarrative = () => {
    // Handled by NarrativePanel
  };

  const handleSaveNarrative = async (editedText) => {
    try {
      // Save to database via API call
      const response = await fetch('/.netlify/functions/saveUnifiedNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          narrative: editedText,
          keyCharacteristics: contextData.unifiedNarrative?.keyCharacteristics || [],
          confidence: contextData.unifiedNarrative?.confidence || 0,
          missingInfo: contextData.unifiedNarrative?.missingInfo || []
        })
      });

      if (!response.ok) throw new Error('Failed to save narrative');

      // Update Redux state
      dispatch({
        type: 'FETCH_UNIFIED_NARRATIVE_SUCCESS',
        payload: {
          modelId,
          narrative: editedText,
          keyCharacteristics: contextData.unifiedNarrative?.keyCharacteristics || [],
          confidence: contextData.unifiedNarrative?.confidence || 0,
          missingInfo: contextData.unifiedNarrative?.missingInfo || []
        }
      });
    } catch (error) {
      console.error('Error saving unified narrative:', error);
    }
  };

  const handleRegenerateNarrative = () => {
    dispatch(generateUnifiedNarrative(modelId, assessmentUuid, contextData));
  };

  // Loading and error states
  if (configLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Text>Loading configuration...</Text>
      </Box>
    );
  }
  
  if (configError) {
    return (
      <Box p="xl">
        <Text c="red" weight={600}>
          Error loading configuration: {configError}
        </Text>
      </Box>
    );
  }

  if (!config || sections.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Text>No configuration found</Text>
      </Box>
    );
  }

  const currentSection = sections[currentStep];

  // Define shared card height
  const CARD_HEIGHT = '600px';

  return (
    <Box p="lg" sx={{ height: '100vh' }}>
      {/* Full-width header */}
      <Group position="apart" mb="lg">
        <div>
          <Title order={3}>Context Assessment</Title>
          <Text color="dimmed">
            Step {currentStep + 1} of {sections.length}: {stepLabels[currentStep]}
          </Text>
        </div>
      </Group>

      {/* Cards Grid with gap */}
      <Grid gutter="sm">
        {/* Left Column */}
        <Grid.Col span={showNarrative ? 6 : 12}>
          <Stack spacing="md">
            <Card 
              withBorder 
              radius="md" 
              p="xl" 
              sx={{ height: CARD_HEIGHT }}
            >
              <Group position="apart" align="center" mb="md">
                <Title order={4}>
                  {stepLabels[currentStep]}
                </Title>
                <Text size="sm" color={currentStepComplete ? 'green' : 'orange'} weight={500}>
                  {currentStepComplete ? '✓ Complete' : '⏳ Incomplete'}
                </Text>
              </Group>
              
              {/* Scrollable Step Content */}
              <Box sx={{ flex: 1, overflowY: 'auto', height: 'calc(100% - 120px)' }}>
                <StepContent
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  section={currentSection}
                  sectionConfig={config?.[currentSection]}
                />
              </Box>
              
              {/* Step Navigation - Fixed at bottom */}
              <Box sx={{ marginTop: 'auto', paddingTop: 'md' }}>
                <StepperNavigation
                  currentStep={currentStep}
                  totalSteps={sections.length}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  stepLabels={stepLabels}
                  canAdvance={currentStepComplete}
                  showStepInfo={false}
                />
              </Box>
            </Card>

            {/* Generate Narrative Button - only when all steps complete */}
            {allStepsComplete && !hasUnifiedNarrative && (
              <Button
                size="lg"
                color="blue"
                leftSection={<AIGeneratingIcon isGenerating={isLoading} size={18} color="white" />}
                onClick={handleGenerateNarrative}
                disabled={isLoading}
                variant="filled"
                fullWidth
              >
                {isLoading ? 'Generating Complete Narrative...' : 'Generate Complete Model Narrative'}
              </Button>
            )}
          </Stack>
        </Grid.Col>

        {/* Right Column - conditional */}
        {showNarrative && (
          <Grid.Col span={6}>
            <NarrativePanel
              visible={showNarrative}
              unifiedNarrative={contextData.unifiedNarrative}
              isLoading={isLoading}
              onEdit={handleEditNarrative}
              onSave={handleSaveNarrative}
              onRegenerate={handleRegenerateNarrative}
              cardHeight={CARD_HEIGHT}
            />
          </Grid.Col>
        )}
      </Grid>
    </Box>
  );
};

export default ContextAssessmentStepper;